/**
 * Multi-page archetype resolution for DropPage — the Page Builder root's
 * archetype, its page labels, and the per-page content-block filter.
 * Extracted verbatim from DropPage against the 400-line ceiling.
 */
import {useMemo} from "react";

/*
 * Generic over the caller's Drop shape so the hook neither imports a type it
 * would have to keep in sync nor widens `contentBlocks` for its consumer.
 */
export function useDropPageArchetype<T extends { pageBuilderData?: unknown; contentBlocks?: Array<{ pageIndex?: number }> }>(
    drop: T | undefined,
    activePageIndex: number,
) {
    /*
     * Drop.pageBuilderData is generated as `object` by openapi-generator (the
     * swagger schema is `additionalProperties`), so it is narrowed locally
     * rather than threading a richer type through the generator. Shape comes
     * from the admin Page Builder root config.
     */
    const pageBuilderData = drop?.pageBuilderData as {
        root?: { props?: { pageArchetype?: string; pages?: Array<{ label?: string }> } };
    } | undefined;
    const pageArchetype = pageBuilderData?.root?.props?.pageArchetype || "single-page";
    const pageLabels = useMemo(() => {
        const pages = pageBuilderData?.root?.props?.pages;
        if (!Array.isArray(pages) || pages.length <= 1) return [];
        return pages.map(p => p.label || "Page");
    }, [pageBuilderData?.root?.props?.pages]);
    const isMultiPage = pageArchetype === "multi-page" && pageLabels.length > 1;

    /* Content blocks belonging to the active page, in multi-page mode only. */
    const activePageBlocks = useMemo(() => {
        if (!drop?.contentBlocks || drop.contentBlocks.length === 0) return [];
        if (!isMultiPage) return drop.contentBlocks;
        return drop.contentBlocks.filter(block => (block.pageIndex ?? 0) === activePageIndex);
    }, [drop?.contentBlocks, isMultiPage, activePageIndex]);

    return {pageArchetype, pageLabels, isMultiPage, activePageBlocks};
}
